import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import ImageUpload from "./ImageUpload";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const CATEGORIES = ["Party", "Concert", "Nightlife", "Day Party", "Karaoke", "Festival", "Holiday", "Special Event"];
const STATUSES = [
  { v: "draft", l: "Draft" }, { v: "regular", l: "Published" }, { v: "featured", l: "Featured" },
  { v: "coming_soon", l: "Coming Soon" }, { v: "sold_out", l: "Sold Out" }, { v: "cancelled", l: "Cancelled" }, { v: "hidden", l: "Hidden" },
];

const EMPTY = {
  name: "", description: "", category: "Party", date: "", start_time: "", end_time: "",
  venue_id: null, flyer_image: "", cover_image: "", gallery: [], status: "draft",
  settings: { age_requirement: "21+", dress_code: "", parking_info: "", food_available: false, bar_available: false, vip_available: false, tables_available: false },
  info_lines: [], tickets: [], recurring_weekly: false, series_id: null,
};

const field = "w-full rounded-md bg-[#141417] border border-[#27272A] px-3 py-2.5 text-white outline-none focus:border-[#D4AF37] text-sm";

export default function EventEditor() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [venues, setVenues] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/venues").then((r) => setVenues(r.data)).catch(() => {});
    if (!isNew) {
      api.get(`/admin/events/${id}`).then((r) => {
        const e = r.data;
        setForm({ ...EMPTY, ...e, settings: { ...EMPTY.settings, ...(e.settings || {}) }, tickets: e.tickets || [] });
      }).catch(() => navigate("/admin/events"));
    }
  }, [id, isNew, navigate]);

  const setS = (k, v) => setForm((f) => ({ ...f, settings: { ...f.settings, [k]: v } }));

  const addTicket = () => setForm((f) => ({ ...f, tickets: [...f.tickets, { name: "", description: "", price: 0, quantity_available: 100, min_purchase: 1, max_purchase: 10, active: true, display_order: f.tickets.length }] }));
  const updTicket = (i, k, v) => setForm((f) => { const t = [...f.tickets]; t[i] = { ...t[i], [k]: v }; return { ...f, tickets: t }; });
  const rmTicket = (i) => setForm((f) => ({ ...f, tickets: f.tickets.filter((_, x) => x !== i) }));

  const save = async (publishStatus) => {
    if (!form.name || !form.date) { toast.error("Event name and date are required."); return; }
    setSaving(true);
    const payload = {
      ...form,
      status: publishStatus || form.status,
      venue_id: form.venue_id || null,
      tickets: form.tickets.map((t) => ({
        ...t, price: parseFloat(t.price) || 0,
        quantity_available: t.quantity_available === "" || t.quantity_available == null ? null : parseInt(t.quantity_available),
        min_purchase: parseInt(t.min_purchase) || 1, max_purchase: parseInt(t.max_purchase) || 10,
      })),
    };
    try {
      if (isNew) { const { data } = await api.post("/admin/events", payload); toast.success("Event created"); navigate(`/admin/events/${data.id}`); }
      else { await api.put(`/admin/events/${id}`, payload); toast.success("Saved"); }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl">
      <button onClick={() => navigate("/admin/events")} className="inline-flex items-center gap-1.5 text-xs font-oswald tracking-widest text-zinc-400 hover:text-white mb-4"><ArrowLeft size={14} /> BACK TO EVENTS</button>
      <h1 className="font-display text-3xl uppercase text-white mb-6">{isNew ? "Create Event" : "Edit Event"}</h1>

      {/* Step 1: Info */}
      <Section title="1 · Event Information">
        <input data-testid="event-name" placeholder="Event Name *" className={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <textarea placeholder="Description" rows={4} className={`${field} mt-3`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <div className="mt-3">
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="event-category" className={field}><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Section>

      {/* Step 2: Date */}
      <Section title="2 · Date & Time">
        <input data-testid="event-date" type="date" className={field} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        <div className="grid grid-cols-2 gap-3 mt-3">
          <input data-testid="event-start" placeholder="Start (e.g. 9:00 PM)" className={field} value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
          <input placeholder="End (e.g. 2:00 AM)" className={field} value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
        </div>
      </Section>

      {/* Step 3: Venue */}
      <Section title="3 · Venue">
        <Select value={form.venue_id || "none"} onValueChange={(v) => setForm({ ...form, venue_id: v === "none" ? null : v })}>
          <SelectTrigger data-testid="event-venue" className={field}><SelectValue placeholder="Choose venue" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No venue / TBA</SelectItem>
            {venues.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}{v.abe_owned ? " (ABE)" : ""}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-xs text-zinc-500 mt-2">Manage venues in the Venues tab.</p>
      </Section>

      {/* Step 4: Media */}
      <Section title="4 · Media">
        <div className="flex gap-4 flex-wrap">
          <ImageUpload label="Flyer" value={form.flyer_image} onChange={(p) => setForm({ ...form, flyer_image: p })} testid="event-flyer-upload" />
          <ImageUpload label="Cover Photo" value={form.cover_image} onChange={(p) => setForm({ ...form, cover_image: p })} testid="event-cover-upload" />
        </div>
      </Section>

      {/* Step 5: Tickets */}
      <Section title="5 · Ticket Types">
        <div className="space-y-3">
          {form.tickets.map((t, i) => (
            <div key={i} data-testid={`ticket-editor-${i}`} className="rounded-lg border border-[#27272A] p-3 bg-[#141417]">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-oswald tracking-widest text-[#D4AF37]">TICKET {i + 1}</span>
                <button onClick={() => rmTicket(i)} className="text-zinc-500 hover:text-red-400"><Trash2 size={14} /></button>
              </div>
              <input data-testid={`ticket-name-${i}`} placeholder="Name (e.g. General Admission)" className={field} value={t.name} onChange={(e) => updTicket(i, "name", e.target.value)} />
              <input placeholder="Description" className={`${field} mt-2`} value={t.description} onChange={(e) => updTicket(i, "description", e.target.value)} />
              <div className="grid grid-cols-3 gap-2 mt-2">
                <div><label className="text-[10px] text-zinc-500">Price ($)</label><input data-testid={`ticket-price-${i}`} type="number" step="0.01" className={field} value={t.price} onChange={(e) => updTicket(i, "price", e.target.value)} /></div>
                <div><label className="text-[10px] text-zinc-500">Qty (blank=∞)</label><input data-testid={`ticket-qty-${i}`} type="number" className={field} value={t.quantity_available ?? ""} onChange={(e) => updTicket(i, "quantity_available", e.target.value)} /></div>
                <div><label className="text-[10px] text-zinc-500">Max/order</label><input type="number" className={field} value={t.max_purchase} onChange={(e) => updTicket(i, "max_purchase", e.target.value)} /></div>
              </div>
              <label className="flex items-center justify-between mt-2"><span className="text-xs text-zinc-400">Active</span><Switch checked={t.active} onCheckedChange={(v) => updTicket(i, "active", v)} /></label>
            </div>
          ))}
        </div>
        <button data-testid="add-ticket-btn" onClick={addTicket} className="mt-3 flex items-center gap-1.5 text-sm text-[#D4AF37] hover:underline"><Plus size={16} /> Add ticket type</button>
      </Section>

      {/* Step 6: Settings */}
      <Section title="6 · Event Settings">
        <div className="grid grid-cols-2 gap-3">
          <input placeholder="Age (e.g. 21+)" className={field} value={form.settings.age_requirement} onChange={(e) => setS("age_requirement", e.target.value)} />
          <input placeholder="Dress Code" className={field} value={form.settings.dress_code} onChange={(e) => setS("dress_code", e.target.value)} />
        </div>
        <input placeholder="Parking Info" className={`${field} mt-3`} value={form.settings.parking_info} onChange={(e) => setS("parking_info", e.target.value)} />
        <div className="grid grid-cols-2 gap-2 mt-3">
          {[["food_available", "Food Available"], ["bar_available", "Full Bar"], ["vip_available", "VIP Available"], ["tables_available", "Tables Available"]].map(([k, l]) => (
            <label key={k} className="flex items-center justify-between rounded-md border border-[#27272A] px-3 py-2"><span className="text-xs text-zinc-300">{l}</span><Switch checked={form.settings[k]} onCheckedChange={(v) => setS(k, v)} /></label>
          ))}
        </div>
      </Section>

      {/* Step 7: Publish */}
      <Section title="7 · Publish">
        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
          <SelectTrigger data-testid="event-status" className={field}><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
        </Select>
        <label className="flex items-center justify-between rounded-md border border-[#27272A] px-3 py-2.5 mt-3">
          <span className="text-sm text-zinc-300">Repeat weekly <span className="text-zinc-500 text-xs">— auto-rebuilds every week</span></span>
          <Switch data-testid="event-recurring" checked={form.recurring_weekly} onCheckedChange={(v) => setForm({ ...form, recurring_weekly: v })} />
        </label>
        <div className="flex gap-3 mt-4">
          <button data-testid="save-draft-btn" disabled={saving} onClick={() => save("draft")} className="flex-1 rounded-md border border-[#27272A] py-3 font-oswald text-sm tracking-widest text-white hover:border-[#D4AF37] disabled:opacity-60">SAVE DRAFT</button>
          <button data-testid="publish-btn" disabled={saving} onClick={() => save(form.status === "draft" ? "featured" : form.status)} className="btn-gold flex-1 rounded-md py-3 font-oswald text-sm tracking-widest disabled:opacity-60">{saving ? "SAVING…" : "PUBLISH EVENT"}</button>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-xl border border-[#27272A] bg-[#18181B] p-4 mb-4">
      <h3 className="font-oswald text-sm tracking-widest text-zinc-300 uppercase mb-3">{title}</h3>
      {children}
    </div>
  );
}
